import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  getCurrentFirmware,
  getProductionFirmware,
} from '../../api/api';
import { useStaticData } from '../../StaticDataProvider';
import { getDeviceType } from '../../Types';
import { DeviceSearch } from '../DeviceSearch/DeviceSearch';
import { DropdownMenu } from '../DropdownMenu/DropdownMenu';
import { useDataRefresh } from '../Hooks/useDataRefresh';

type FirmwareCellValue =
  string | 'error' | 'notfound' | 'networkissue' | null;

export const ProductOverview = () => {
  const staticData = useStaticData();
  const navigate = useNavigate();
  const [refreshKey, requestRefresh] = useDataRefresh();
  const [currentFirmwareMap, setCurrentFirmwareMap] = useState<
    Map<string, FirmwareCellValue>
  >(new Map());
  const [productionFirmwareMap, setProductionFirmwareMap] = useState<
    Map<string, FirmwareCellValue>
  >(new Map());
  const [currentFirmwareErrors, setCurrentFirmwareErrors] = useState<
    Map<string, string>
  >(new Map());
  const [productionFirmwareErrors, setProductionFirmwareErrors] =
    useState<Map<string, string>>(new Map());
  const [isRefreshing, setIsRefreshing] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const fetchFirmwareData = async () => {
      setIsRefreshing(true);
      setCurrentFirmwareErrors(new Map());
      setProductionFirmwareErrors(new Map());

      await Promise.all(
        staticData.products.flatMap((p) => {
          const key = `${p.code}_${p.variant}`;

          const fetchCurrent = async () => {
            let value: FirmwareCellValue;
            let errorMessage: string | undefined;
            try {
              const currentResult = await getCurrentFirmware(
                p.code,
                p.variant,
              );
              if (
                currentResult.status === 'OK' &&
                currentResult.data?.isCurrent
              ) {
                value = currentResult.data.firmwareVersion;
              } else if (currentResult.status === 'Error') {
                console.error(
                  `Failed to fetch current firmware for ${p.code}/${p.variant}:`,
                  currentResult.errorMessages,
                );
                value = 'error';
                errorMessage = currentResult.errorMessages.join(', ');
              } else if (currentResult.status === 'Not Found') {
                value = 'notfound';
                console.warn(
                  `Not Found returned for ${p.code}/${p.variant}:`,
                  currentResult.errorMessages,
                );
              } else if (currentResult.status === 'Network issue') {
                value = 'networkissue';
                errorMessage = currentResult.errorMessages.join(', ');
                console.warn(
                  `Network issue returned for ${p.code}/${p.variant}:`,
                  currentResult.errorMessages,
                );
              } else {
                value = null;
              }
            } catch (e) {
              console.error(
                `Error fetching current firmware for ${p.code}/${p.variant}:`,
                e,
              );
              value = 'error';
              errorMessage =
                'Unexpected error while loading current firmware.';
            }

            if (cancelled) return;

            if (errorMessage) {
              setCurrentFirmwareMap((prev) => {
                if (prev.has(key)) return prev;
                return new Map(prev).set(key, value);
              });
              setCurrentFirmwareErrors((prev) =>
                new Map(prev).set(key, errorMessage),
              );
            } else {
              setCurrentFirmwareMap((prev) =>
                new Map(prev).set(key, value),
              );
            }
          };

          const fetchProduction = async () => {
            let value: FirmwareCellValue;
            let errorMessage: string | undefined;
            try {
              const prodResult = await getProductionFirmware(
                p.code,
                p.variant,
              );
              if (prodResult.status === 'OK' && prodResult.data) {
                if (!prodResult.data.version) {
                  console.warn(
                    `Production firmware response for ${p.code}/${p.variant} has no 'version' field:`,
                    prodResult.data,
                  );
                }
                value = prodResult.data.version;
              } else if (prodResult.status === 'Error') {
                console.error(
                  `Failed to fetch production firmware for ${p.code}/${p.variant}:`,
                  prodResult.errorMessages,
                );
                value = 'error';
                errorMessage = prodResult.errorMessages.join(', ');
              } else if (prodResult.status === 'Not Found') {
                value = 'notfound';
                console.warn(
                  `Not Found returned for production firmware for ${p.code}/${p.variant}:`,
                  prodResult.errorMessages,
                );
              } else if (prodResult.status === 'Network issue') {
                value = 'networkissue';
                errorMessage = prodResult.errorMessages.join(', ');
                console.warn(
                  `Network issue returned for production firmware for ${p.code}/${p.variant}:`,
                  prodResult.errorMessages,
                );
              } else {
                value = null;
              }
            } catch (e) {
              console.error(
                `Error fetching production firmware for ${p.code}/${p.variant}:`,
                e,
              );
              value = 'error';
              errorMessage =
                'Unexpected error while loading production firmware.';
            }

            if (cancelled) return;

            if (errorMessage) {
              setProductionFirmwareMap((prev) => {
                if (prev.has(key)) return prev;
                return new Map(prev).set(key, value);
              });
              setProductionFirmwareErrors((prev) =>
                new Map(prev).set(key, errorMessage),
              );
            } else {
              setProductionFirmwareMap((prev) =>
                new Map(prev).set(key, value),
              );
            }
          };

          return [fetchCurrent(), fetchProduction()];
        }),
      );
      if (!cancelled) {
        setIsRefreshing(false);
      }
    };

    void fetchFirmwareData();

    return () => {
      cancelled = true;
    };
  }, [refreshKey, staticData.products]);

  return (
    <>
      <div className="inset-x-0 w-full top-0 h-16">
        <DropdownMenu />
      </div>
      <div className="md:container md:mx-auto">
        <DeviceSearch />
        <div className="flex items-center justify-end gap-3 px-3.5 pt-3.5">
          {(currentFirmwareErrors.size > 0 ||
            productionFirmwareErrors.size > 0) && (
            <div className="text-sm text-red-600" role="alert">
              Some product firmware data could not be refreshed. Try
              again.
            </div>
          )}
          <button
            type="button"
            onClick={requestRefresh}
            disabled={isRefreshing}
            className="text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
          >
            {isRefreshing ? 'Refreshing...' : 'Refresh product data'}
          </button>
        </div>
        <section className="table w-full p-3.5">
          <table className="w-full border">
            <thead>
              <tr className="bg-gray-50 border-b">
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Code
                </th>
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Name
                </th>
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Variant
                </th>
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Device Type
                </th>
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Current
                </th>
                <th className="p-2 border-r text-sm font-thin text-gray-500">
                  Production Firmware
                </th>
              </tr>
            </thead>
            <tbody>
              {[...staticData.products]
                .sort((x, y) => x.code - y.code)
                .map((p) => {
                  const key = `${p.code}_${p.variant}`;
                  const currentFirmware = currentFirmwareMap.get(key);
                  const prodFirmware = productionFirmwareMap.get(key);
                  const currentFirmwareError =
                    currentFirmwareErrors.get(key);
                  const productionFirmwareError =
                    productionFirmwareErrors.get(key);
                  return (
                    <tr
                      key={p.code + p.variant}
                      className="bg-gray-50 text-center hover:bg-gray-200 cursor-pointer"
                      onClick={(event) => {
                        if (
                          (event.target as HTMLElement).closest('a')
                        ) {
                          return;
                        }
                        navigate(
                          `/Products/${p.code}/Variant/${p.variant}`,
                        );
                      }}
                    >
                      <td className="p-2 border-r">
                        <Link
                          className="text-blue-600 hover:underline"
                          to={`/Products/${p.code}/Variant/${p.variant}`}
                        >
                          {p.code}
                        </Link>
                      </td>
                      <td className="p-2 border-r">{p.name}</td>
                      <td className="p-2 border-r">{p.variant}</td>
                      <td className="p-2 border-r">
                        {getDeviceType(p.code.toString())}
                      </td>
                      <td className="p-2 border-r">
                        {currentFirmwareError &&
                        currentFirmware !== undefined ? (
                          <span title={currentFirmwareError}>
                            {formatFirmwareCellValue(currentFirmware)}
                            <span className="text-red-500"> *</span>
                          </span>
                        ) : currentFirmware === undefined ? (
                          '...'
                        ) : (
                          formatFirmwareCellValue(currentFirmware)
                        )}
                      </td>
                      <td className="p-2 border-r">
                        {productionFirmwareError &&
                        prodFirmware !== undefined ? (
                          <span title={productionFirmwareError}>
                            {formatFirmwareCellValue(prodFirmware)}
                            <span className="text-red-500"> *</span>
                          </span>
                        ) : prodFirmware === undefined ? (
                          '...'
                        ) : (
                          formatFirmwareCellValue(prodFirmware)
                        )}
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </section>
      </div>
    </>
  );
};

function formatFirmwareCellValue(value: FirmwareCellValue) {
  if (value === 'error') {
    return <span className="text-red-500">Error</span>;
  }
  if (value === 'notfound') {
    return <span className="text-yellow-500">Not set</span>;
  }
  if (value === 'networkissue') {
    return (
      <span className="text-yellow-500">Network/firewall issue</span>
    );
  }

  return value ?? '-';
}
